import { useAtom } from "@dbeining/react-atom";
import { Field, Form, Formik } from "formik";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import utc from "dayjs/plugin/utc";
import { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Col, Container, Row } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { Link, useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import type {
    IMunicipalDegradableDetail,
    MunicipalDegradableUpdatePayload,
} from "@/customTypings/reference/MunicipalDegradable";
import { userAtom } from "../../atoms";
import {
    EditFormLifecycleActions,
    EditFormSaveCancelButtons,
    EditFormSplitFooter,
    shouldShowEditFormLifecycleActions,
} from "../../components/Forms";
import { RecordAuditMetaBar } from "../../components/Layout/RecordAuditMetaBar";
import { ResponsiveBreadcrumb } from "../../components/Layout/ResponsiveBreadcrumb";
import { LoadingSpinner } from "../../components/common/Loading/LoadingSpinner";
import { ConfirmationModel } from "../../components/features/common";
import { EllipsisActionMenu } from "../../components/UI/EllipsisActionMenu";
import { useViewport } from "../../contexts";
import { IRole } from "../../customTypings/user/Role";
import { useFormAutoFocus } from "@/hooks/common/useFormAutoFocus";
import { FormikUnsavedChangesGuard, type LeaveSaveOutcome } from "@/features/unsavedChanges";
import { municipalDegradablesService } from "../../services/reference/municipalDegradables.service";
import {
    appRoutes,
    arePayloadsEqual,
    buildRecordLifecycleConfirmProps,
    DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE,
    editFormBreadcrumbLabel,
    editPageTitle,
    EntityLabels,
    formatReferenceSourceDisplay,
    getUserFacingErrorMessage,
    isUnchangedUpdateResponse,
    ListPageTitles,
    logErrorForDev,
    NO_CHANGES_TOAST_MESSAGE,
    referenceAdminListPath,
    Role,
    shouldShowFormFieldError,
    showToastError,
    showToastSuccess,
    showToastUnchanged,
} from "../../utils";
import {
    buildMdAdminPagedListActions,
    formatMdToastLabel,
    getMdAdminActionSuccessMessage,
    type MdAdminConfirmKind,
} from "../MunicipalDegradablesListPage/mdAdminListActions";

dayjs.extend(advancedFormat);
dayjs.extend(utc);

function formatMdMetaDate(iso: string | null | undefined): string {
    if (!iso) return "";
    return dayjs.utc(iso).local().format("Do MMMM YYYY [at] HH:mm");
}

function formatEffectiveFromDate(iso: string | null | undefined): string {
    if (!iso) return "";
    return dayjs.utc(iso).format("Do MMMM YYYY");
}

function formatByLine(name: string | null | undefined): string {
    const t = typeof name === "string" ? name.trim() : "";
    return t.length > 0 ? t : "Unknown";
}

type FormValues = { id?: string; description: string };

function buildUpdatePayload(values: FormValues): MunicipalDegradableUpdatePayload {
    const description = typeof values.description === "string" ? values.description.trim() : values.description;
    return { id: values.id!, description };
}

function buildUpdatePayloadFromDetail(detail: IMunicipalDegradableDetail): MunicipalDegradableUpdatePayload {
    return { id: detail.id, description: detail.description };
}

const mdEditValidationSchema = Yup.object({
    id: Yup.string().optional(),
    description: Yup.string()
        .transform((v) => (typeof v === "string" ? v.trim() : v))
        .required("Description is required.")
        .max(255, "Description must be at most 255 characters."),
});

type ConfirmKind = MdAdminConfirmKind | null;

const MunicipalDegradableEditPage = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { width } = useViewport();
    const isNarrowScreen = width < 768;
    const currentUser = useAtom(userAtom);
    const isAdmin = currentUser?.identity.roles.some(
        (role: IRole) => role.name === Role.Admin || role.name === Role.SystemAdmin
    );

    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [initialValues, setInitialValues] = useState<FormValues>({ description: "" });
    const [indicatorType, setIndicatorType] = useState("");
    const [applies, setApplies] = useState("");
    const [metaActive, setMetaActive] = useState(true);
    const [metaDeleted, setMetaDeleted] = useState(false);
    const [source, setSource] = useState("");
    const [sourceVersion, setSourceVersion] = useState("");
    const [effectiveFromUtc, setEffectiveFromUtc] = useState<string | null>(null);
    const [createdAt, setCreatedAt] = useState<string | null>(null);
    const [updatedAt, setUpdatedAt] = useState<string | null>(null);
    const [createdByDisplayName, setCreatedByDisplayName] = useState<string | null>(null);
    const [lastUpdatedByDisplayName, setLastUpdatedByDisplayName] = useState<string | null>(null);
    const [deletedAuditAtUtc, setDeletedAuditAtUtc] = useState<string | null>(null);
    const [deletedByDisplayName, setDeletedByDisplayName] = useState<string | null>(null);
    const [disabledAuditAtUtc, setDisabledAuditAtUtc] = useState<string | null>(null);
    const [disabledByDisplayName, setDisabledByDisplayName] = useState<string | null>(null);

    const [confirmKind, setConfirmKind] = useState<ConfirmKind>(null);
    const [liveValidationAfterSubmit, setLiveValidationAfterSubmit] = useState(false);
    const savedUpdatePayloadRef = useRef<MunicipalDegradableUpdatePayload | null>(null);
    const leaveAfterSaveRef = useRef(false);
    const saveOutcomeRef = useRef<LeaveSaveOutcome>(null);

    const validationSchema = useMemo(() => mdEditValidationSchema, []);

    const formAutoFocusRef = useFormAutoFocus(!loading, [loading, id]);

    const headerMenuItem = useMemo(() => ({ isActive: metaActive, isDeleted: metaDeleted }), [metaActive, metaDeleted]);

    const headerMenuActions = useMemo(
        () => buildMdAdminPagedListActions((kind) => setConfirmKind(kind)),
        []
    );

    const applyMdDetail = (row: IMunicipalDegradableDetail) => {
        setInitialValues({ id: row.id, description: row.description });
        setIndicatorType(row.indicatorType);
        setApplies(row.applies);
        setMetaActive(row.isActive);
        setMetaDeleted(row.isDeleted);
        setSource(row.source);
        setSourceVersion(row.sourceVersion);
        setEffectiveFromUtc(row.effectiveFromUtc);
        setCreatedAt(row.createdAtUtc);
        setUpdatedAt(row.updatedAtUtc);
        setCreatedByDisplayName(row.createdByDisplayName ?? null);
        setLastUpdatedByDisplayName(row.lastUpdatedByDisplayName ?? null);
        setDeletedAuditAtUtc(row.deletedAuditAtUtc ?? null);
        setDeletedByDisplayName(row.deletedByDisplayName ?? null);
        setDisabledAuditAtUtc(row.disabledAuditAtUtc ?? null);
        setDisabledByDisplayName(row.disabledByDisplayName ?? null);
        savedUpdatePayloadRef.current = buildUpdatePayloadFromDetail(row);
    };

    useEffect(() => {
        if (!id) {
            setLoading(false);
            setLoadError("Indicator id is required.");
            return;
        }
        setLoading(true);
        setLoadError(null);
        municipalDegradablesService
            .getById(id)
            .then(applyMdDetail)
            .catch((e: unknown) => {
                logErrorForDev("MunicipalDegradableEditPage.load", e);
                setLoadError(getUserFacingErrorMessage(e, "Could not load indicator."));
            })
            .finally(() => setLoading(false));
    }, [id]);

    const confirmProps = useMemo(
        () =>
            buildRecordLifecycleConfirmProps(
                confirmKind,
                EntityLabels.municipalDegradableIndicator,
                formatMdToastLabel(indicatorType, applies)
            ),
        [confirmKind, indicatorType, applies]
    );

    const runMutation = (kind: ConfirmKind) => {
        if (!id || !kind) return;
        const op =
            kind === "disable"
                ? municipalDegradablesService.disable(id)
                : municipalDegradablesService.enable(id);
        op.then(() => {
            setConfirmKind(null);
            showToastSuccess(getMdAdminActionSuccessMessage(kind, indicatorType, applies));
            return municipalDegradablesService.getById(id).then(applyMdDetail);
        }).catch((e: unknown) => { setConfirmKind(null); showToastError(getUserFacingErrorMessage(e, DEFAULT_ACTION_FAILED_MESSAGE));
        });
    };

    if (!isAdmin) {
        return (
            <Container fluid className="my-3">
                <Alert variant="warning">You do not have permission to manage municipal/degradable indicators.</Alert>
            </Container>
        );
    }

    if (loading) {
        return (
            <Container fluid className="my-5 d-flex justify-content-center">
                <LoadingSpinner text="Loading indicator…" />
            </Container>
        );
    }

    if (loadError) {
        return (
            <Container fluid className="my-3">
                <Alert variant="danger">{loadError}</Alert>
                <Link to={referenceAdminListPath("municipalDegradables")}>
                    Back to {ListPageTitles.municipalDegradableIndicators}
                </Link>
            </Container>
        );
    }

    return (
        <Container fluid>
            <Helmet>
                <title>{editPageTitle(EntityLabels.municipalDegradableIndicator, false)}</title>
            </Helmet>
            <ResponsiveBreadcrumb
                className="mb-3"
                items={[
                    { text: EntityLabels.referenceData, to: appRoutes.codes },
                    {
                        text: ListPageTitles.municipalDegradableIndicators,
                        to: referenceAdminListPath("municipalDegradables"),
                    },
                    { text: editFormBreadcrumbLabel(false) },
                ]}
            />

            <div
                className="d-flex align-items-center justify-content-between gap-2 mb-3 w-100"
                style={{
                    borderBottom: "2px solid var(--primary)",
                    paddingBottom: "0.5rem",
                }}
            >
                <h1
                    className="mb-0 flex-grow-1 min-w-0"
                    style={{ borderBottom: "none", paddingBottom: 0 }}
                >
                    {editPageTitle(EntityLabels.municipalDegradableIndicator, false)}
                </h1>
                {isNarrowScreen && Boolean(initialValues.id?.trim()) && (
                    <EllipsisActionMenu
                        id="md-edit-actions-dropdown"
                        ariaLabel="Indicator actions"
                        actions={headerMenuActions}
                        item={headerMenuItem}
                    />
                )}
            </div>

            <RecordAuditMetaBar
                formatDate={formatMdMetaDate}
                formatByLine={formatByLine}
                createdAt={createdAt}
                createdByDisplayName={createdByDisplayName}
                updatedAt={updatedAt}
                lastUpdatedByDisplayName={lastUpdatedByDisplayName}
                isDeleted={metaDeleted}
                isEnabled={metaActive}
                deletedAuditAtUtc={deletedAuditAtUtc}
                deletedByDisplayName={deletedByDisplayName}
                disabledAuditAtUtc={disabledAuditAtUtc}
                disabledByDisplayName={disabledByDisplayName}
            />

            <Formik<FormValues>
                enableReinitialize
                initialValues={initialValues}
                validationSchema={validationSchema}
                validateOnMount={false}
                validateOnChange
                validateOnBlur={false}
                onSubmit={async (values, { resetForm }) => {
                    saveOutcomeRef.current = null;
                    setLiveValidationAfterSubmit(true);
                    try {
                        if (values.id) {
                            const updatePayload = buildUpdatePayload(values);
                            if (arePayloadsEqual(updatePayload, savedUpdatePayloadRef.current)) {
                                setLiveValidationAfterSubmit(false);
                                saveOutcomeRef.current = "unchanged";
                                showToastUnchanged(NO_CHANGES_TOAST_MESSAGE);
                                if (leaveAfterSaveRef.current) return;
                                return;
                            }
                            const result = await municipalDegradablesService.update(updatePayload);
                            if (isUnchangedUpdateResponse(result)) {
                                setLiveValidationAfterSubmit(false);
                                saveOutcomeRef.current = "unchanged";
                                showToastUnchanged(result.message ?? NO_CHANGES_TOAST_MESSAGE);
                                if (leaveAfterSaveRef.current) return;
                                return;
                            }
                            setLiveValidationAfterSubmit(false);
                            saveOutcomeRef.current = "success";
                            savedUpdatePayloadRef.current = updatePayload;
                            showToastSuccess(
                                `${formatMdToastLabel(indicatorType, applies)} updated successfully!`
                            );
                            resetForm({ values });
                            if (leaveAfterSaveRef.current) return;
                            navigate(referenceAdminListPath("municipalDegradables"));
                        }
                    } catch (e: unknown) { saveOutcomeRef.current = "failure";
                        showToastError(getUserFacingErrorMessage(e, DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE));
                    }
                }}
            >
                {({ isSubmitting, values, errors, touched, submitCount, handleSubmit, validateForm, setTouched }) => {
                    const hasPersistedRecord = Boolean(values.id?.trim());
                    const showErr = (field: keyof FormValues) =>
                        shouldShowFormFieldError(
                            errors,
                            field as keyof typeof errors,
                            hasPersistedRecord,
                            submitCount,
                            liveValidationAfterSubmit,
                            touched
                        );
                    const form = (
                        <Form
                            ref={formAutoFocusRef}
                            noValidate
                            onSubmit={handleSubmit}
                        >
                            <Row className="g-3">
                                <Col md={4}>
                                    <label className="form-label mb-0">Source</label>
                                    <div className="form-control-plaintext py-0">
                                        {formatReferenceSourceDisplay(source) || "-"}
                                    </div>
                                </Col>
                                <Col md={4}>
                                    <label className="form-label mb-0">Source version</label>
                                    <div className="form-control-plaintext py-0">{sourceVersion || "-"}</div>
                                </Col>
                                <Col md={4}>
                                    <label className="form-label mb-0">Effective from</label>
                                    <div className="form-control-plaintext py-0">
                                        {effectiveFromUtc ? formatEffectiveFromDate(effectiveFromUtc) : "-"}
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <label className="form-label mb-0">Indicator</label>
                                    <div className="form-control-plaintext py-0 fw-semibold">{indicatorType || "-"}</div>
                                </Col>
                                <Col md={6}>
                                    <label className="form-label mb-0">Applies</label>
                                    <div className="form-control-plaintext py-0 fw-semibold">{applies || "-"}</div>
                                </Col>
                                <Col xs={12}>
                                    <label className="form-label" htmlFor="description">
                                        Description <span className="text-danger" aria-hidden="true">*</span>
                                        <span className="visually-hidden"> (required)</span>
                                    </label>
                                    <Field
                                        id="description"
                                        name="description"
                                        as="textarea"
                                        rows={3}
                                        className={`form-control${showErr("description") ? " is-invalid" : ""}`}
                                        aria-required
                                        aria-invalid={showErr("description")}
                                    />
                                    {showErr("description") && (
                                        <div className="invalid-feedback d-block">{errors.description}</div>
                                    )}
                                </Col>
                            </Row>

                            <EditFormSplitFooter
                                start={
                                    shouldShowEditFormLifecycleActions(false, values.id, isNarrowScreen) ? (
                                        <div className="d-flex flex-wrap gap-2">
                                            <EditFormLifecycleActions
                                                metaDeleted={metaDeleted}
                                                metaEnabled={metaActive}
                                                showDeleteRestore={false}
                                                onAction={setConfirmKind}
                                            />
                                        </div>
                                    ) : undefined
                                }
                            >
                                <EditFormSaveCancelButtons
                                    isSubmitting={isSubmitting}
                                    disabled={metaDeleted}
                                    readOnlyForm={metaDeleted}
                                    enableSaveBeforeLeave
                                    leaveAfterSaveRef={leaveAfterSaveRef}
                                    saveOutcomeRef={saveOutcomeRef}
                                    onCancel={() => navigate(referenceAdminListPath("municipalDegradables"))}
                                />
                            </EditFormSplitFooter>
                        </Form>
                    );

                    return (
                        <FormikUnsavedChangesGuard
                            enableRouteBlocking
                            readOnlyForm={metaDeleted}
                            isNewRecord={false}
                            newRecordLabel={EntityLabels.municipalDegradableIndicator}
                            enableSaveBeforeLeave
                            leaveAfterSaveRef={leaveAfterSaveRef}
                            saveOutcomeRef={saveOutcomeRef}
                        >
                            {form}
                        </FormikUnsavedChangesGuard>
                    );
                }}
            </Formik>

            <ConfirmationModel
                show={confirmKind !== null}
                onClose={(confirmed) => {
                    if (confirmed) runMutation(confirmKind);
                    else setConfirmKind(null);
                }}
                title={confirmProps.title}
                body={confirmProps.body}
                buttonSuccessVariant={confirmProps.buttonSuccessVariant}
                buttonSuccessText={confirmProps.buttonSuccessText}
                buttonCancelText="Cancel"
            />
        </Container>
    );
};

export { MunicipalDegradableEditPage };
